import { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    fetchBizimhesapSettings,
    upsertBizimhesapSettings,
    testBizimhesapConnection,
    fetchBizimhesapSyncQueue,
    retryBizimhesapSyncItem,
} from "../../../api/requests/bizimhesap-requests";

export const useBizimhesapSettings = (companyId, onSaved) => {
    const [loading, setLoading] = useState(false);
    const [hasSettings, setHasSettings] = useState(false);
    const [tokenDefined, setTokenDefined] = useState(false);
    const [testing, setTesting] = useState(false);
    // Bağlantı testi sonucu modalda KALICI olarak gösterilir. Toast tek kanal olsaydı
    // 3 saniye sonra kaybolur, kullanıcı hata kodunu ve çözüm önerisini okuyamazdı.
    const [testResult, setTestResult] = useState(null);

    // Senkron kuyruğu state'leri
    const [queue, setQueue] = useState({ content: [], totalElements: 0, totalPages: 0, page: 0, size: 10 });
    const [queueLoading, setQueueLoading] = useState(false);
    const [queueStatus, setQueueStatus] = useState("");
    const [retryingId, setRetryingId] = useState(null);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            companyId: companyId || "",
            firmId: "",
            token: "",
            enabled: true,
            collectionSyncEnabled: false,
        },
        validationSchema: Yup.object({
            companyId: Yup.string().required("Gerekli"),
            firmId: Yup.string().required("Firm ID gerekli"),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = {
                    companyId: values.companyId,
                    firmId: values.firmId,
                    // Token boş bırakılırsa backend mevcut token'ı korur
                    token: values.token || null,
                    enabled: values.enabled,
                    collectionSyncEnabled: values.collectionSyncEnabled,
                };

                const res = await upsertBizimhesapSettings(payload);
                if (res?.data) {
                    setHasSettings(true);
                    setTokenDefined(res.data.tokenDefined ?? tokenDefined);
                    toast.success("Bizimhesap ayarları kaydedildi");
                    onSaved?.();
                } else {
                    toast.error(res?.message || "Bizimhesap ayarları kaydedilemedi");
                }
            } catch (error) {
                toast.error("Bizimhesap ayarları kaydedilirken hata oluştu");
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (!companyId) return;

        const loadSettings = async () => {
            try {
                const res = await fetchBizimhesapSettings(companyId);

                if (res?.data) {
                    setHasSettings(true);
                    setTokenDefined(res.data.tokenDefined ?? false);
                    // Güvenlik: token backend'den DÖNMEZ; boş bırakılır,
                    // değiştirmek için yeniden girilmesi gerekir.
                    formik.setValues({
                        companyId,
                        firmId: res.data.firmId ?? "",
                        token: "",
                        enabled: res.data.enabled ?? true,
                        collectionSyncEnabled: res.data.collectionSyncEnabled ?? false,
                    });
                } else {
                    setHasSettings(false);
                }
            } catch (error) {
                console.error("Bizimhesap ayarları yüklenemedi", error);
                setHasSettings(false);
            }
        };

        loadSettings();
    }, [companyId]);

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await testBizimhesapConnection(companyId);
            // Backend her durumda dolu bir sonuç döner (4xx/5xx gövdesi dahil):
            // { ok, code, message, suggestion }
            const result = res?.data;

            if (result?.ok) {
                setTestResult({ ok: true, message: result.message || "Bağlantı başarıyla doğrulandı." });
                toast.success(result.message || "Bağlantı başarıyla doğrulandı.");
                return;
            }

            const failure = {
                ok: false,
                code: result?.code || null,
                // Beklenmedik bir gövde gelirse GenericResponse.message'a düşülür.
                message: result?.message || res?.message || "Bağlantı doğrulanamadı.",
                suggestion: result?.suggestion || null,
            };
            setTestResult(failure);
            toast.error(failure.message);
        } catch (error) {
            // Ağ kesintisi / gövde parse edilemedi: istek backend'e hiç ulaşmamış olabilir.
            const failure = {
                ok: false,
                code: null,
                message: "Bağlantı testi yapılamadı.",
                suggestion: "İnternet bağlantınızı kontrol edip tekrar deneyin.",
            };
            setTestResult(failure);
            toast.error(failure.message);
        } finally {
            setTesting(false);
        }
    };

    const loadQueue = useCallback(async (page = 0, status = queueStatus, size = queue.size) => {
        if (!companyId) return;
        setQueueLoading(true);
        try {
            const res = await fetchBizimhesapSyncQueue(companyId, { status, page, size });
            if (res?.data) {
                setQueue({
                    content: Array.isArray(res.data.content) ? res.data.content : [],
                    totalElements: res.data.totalElements ?? 0,
                    totalPages: res.data.totalPages ?? 0,
                    page: res.data.page ?? page,
                    size: res.data.size ?? size,
                });
            }
        } catch (error) {
            console.error("Bizimhesap kuyruğu yüklenemedi", error);
        } finally {
            setQueueLoading(false);
        }
    }, [companyId, queueStatus, queue.size]);

    const changeQueueStatus = (status) => {
        setQueueStatus(status);
        loadQueue(0, status);
    };

    const retryItem = async (queueId) => {
        setRetryingId(queueId);
        try {
            const res = await retryBizimhesapSyncItem(companyId, queueId);
            if (res?.data === true) {
                toast.success("Kayıt yeniden denemeye alındı");
                loadQueue(queue.page);
            } else {
                toast.error(res?.message || "Yeniden deneme başarısız");
            }
        } catch (error) {
            toast.error("Yeniden deneme sırasında hata oluştu");
        } finally {
            setRetryingId(null);
        }
    };

    return {
        formik,
        loading,
        hasSettings,
        tokenDefined,
        testing,
        testConnection,
        testResult,
        clearTestResult: () => setTestResult(null),
        queue,
        queueLoading,
        queueStatus,
        changeQueueStatus,
        loadQueue,
        retryingId,
        retryItem,
    };
};
